// Thin fetch wrapper that prepends the API base and a JWT bearer token (if any).
// Reads token from localStorage so it survives reloads. Auto-redirects to /login
// on 401 (handled by AuthProvider's interceptor; here we just throw).
import { apiBase } from './config';
import { detailText } from './errors.js';

// status 0 means the request never got a response (network down, CORS, DNS).
// Use errorMessage() from errors.js to show one of these to a user.
export class ApiError extends Error {
  constructor(status, statusText, payload) {
    super(detailText(payload?.detail) || statusText || (status ? `HTTP ${status}` : 'Network error'));
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.payload = payload;
  }
}

function getToken() {
  return localStorage.getItem('token') || '';
}

// Access tokens live 30 minutes. On a 401 the refresh token (7 days) buys a new pair once, and
// the request is repeated; only when that fails is the user sent to /login. Concurrent 401s
// share one refresh call (the API rotates the refresh token on every use).
const NO_REFRESH = /^\/auth\/(login|refresh|logout|forgot-password|reset-password|verify-email)/;
let refreshing = null;

function refreshTokens() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = fetch(`${apiBase()}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = await res.json();
        localStorage.setItem('token', data.access_token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

export async function api(path, opts = {}) {
  const { _retried, ...fetchOpts } = opts;
  opts = fetchOpts;
  const base = apiBase();
  const url = path.startsWith('http') ? path : `${base}${path}`;
  const headers = new Headers(opts.headers || {});
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  const isFormLike =
    opts.body instanceof FormData ||
    opts.body instanceof URLSearchParams ||
    opts.body instanceof Blob;
  if (opts.body && !headers.has('Content-Type') && !isFormLike) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const body =
    opts.body && !isFormLike && typeof opts.body !== 'string'
      ? JSON.stringify(opts.body)
      : opts.body;
  let res;
  try {
    res = await fetch(url, { ...opts, headers, body });
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
    throw new ApiError(0, '', null);
  }
  const ct = res.headers.get('content-type') || '';
  if (res.status === 204) return null;
  const payload = ct.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => '');
  if (!res.ok) {
    if (res.status === 401 && !_retried && !NO_REFRESH.test(path) && (await refreshTokens())) {
      return api(path, { ...opts, _retried: true });
    }
    if (res.status === 401) {
      // Surface a custom event so the AuthProvider can react globally.
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    throw new ApiError(res.status, res.statusText, payload);
  }
  return payload;
}

export const get = (path, opts) => api(path, { ...opts, method: 'GET' });
export const post = (path, body, opts) => api(path, { ...opts, method: 'POST', body });
export const patch = (path, body, opts) => api(path, { ...opts, method: 'PATCH', body });
export const put = (path, body, opts) => api(path, { ...opts, method: 'PUT', body });
export const del = (path, opts) => api(path, { ...opts, method: 'DELETE' });
